import React, { useEffect, useState } from "react";
import { logError } from "../lib/log";
import { Link, Navigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Users, ArrowLeft, ChevronDown, ChevronRight } from "lucide-react";

function YearDetail({ year, onClose }) {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState({});
  useEffect(() => {
    api.get(`/teacher/roster-by-year/${year}`).then((r) => setData(r.data)).catch(() => {});
  }, [year]);
  if (!data) return <div className="text-slate-400">Loading…</div>;
  return (
    <div className="mt-4 rounded-md border border-white/10 bg-black/40 p-5" data-testid={`roster-year-detail-${year}`}>
      <div className="flex items-center justify-between">
        <div className="font-display text-lg font-semibold text-white">Year {year} · per-lesson breakdown ({data.class_size} in class)</div>
        <button onClick={onClose} className="rounded-md border border-white/10 bg-white/5 px-3 py-1 text-xs hover:bg-white/10">Close</button>
      </div>
      <div className="mt-4 space-y-1">
        {data.lessons.map((l) => {
          const pct = data.class_size ? Math.round((l.readers.length / data.class_size) * 100) : 0;
          const isOpen = !!expanded[l.order];
          return (
            <div key={l.order} className="rounded-md border border-white/5 bg-black/60" data-testid={`roster-lesson-${l.order}`}>
              <button
                onClick={() => setExpanded({ ...expanded, [l.order]: !isOpen })}
                className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-white/5"
              >
                <span className="font-mono-num w-10 text-xs text-slate-500">{l.month}·{String(l.order).padStart(2, "0")}</span>
                {isOpen ? <ChevronDown size={12} className="text-slate-400" /> : <ChevronRight size={12} className="text-slate-400" />}
                <span className="flex-1 text-sm text-slate-200">{l.title}</span>
                <span className="h-1.5 w-20 overflow-hidden rounded-full bg-white/5"><span className="block h-full bg-lime-400" style={{ width: `${pct}%` }} /></span>
                <span className="font-mono-num w-12 text-right text-xs text-lime-200">{l.readers.length}/{data.class_size}</span>
              </button>
              {isOpen && (
                <div className="border-t border-white/5 px-4 py-2 text-xs">
                  {l.readers.length === 0 ? (
                    <div className="py-2 text-slate-500">Nobody yet.</div>
                  ) : (
                    <ul className="space-y-1">
                      {l.readers.map((r) => (
                        <li key={r.user_id} className="flex items-center justify-between">
                          <span className="text-slate-200">{r.name}</span>
                          <span className="font-mono-num text-slate-500">{new Date(r.completed_at).toLocaleDateString()}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Roster() {
  const { user, loading } = useAuth();
  const [students, setStudents] = useState([]);
  const [openYear, setOpenYear] = useState(null);

  useEffect(() => {
    if (user?.role === "teacher") api.get("/teacher/roster").then((r) => setStudents(r.data.students)).catch((e) => logError("Roster load failed", e));
  }, [user]);

  if (loading) return <div className="text-slate-400">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "teacher") return <Navigate to="/dashboard" replace />;

  return (
    <div className="space-y-8" data-testid="roster-root">
      <div>
        <Link to="/teacher" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-slate-400 hover:text-cyan-300">
          <ArrowLeft size={14} /> Teacher
        </Link>
        <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-500/10 px-3 py-1 text-xs uppercase tracking-widest text-cyan-300">
          <Users size={12} /> Class roster
        </div>
        <h1 className="font-display mt-3 text-4xl font-bold tracking-tight sm:text-5xl">Who's on track?</h1>
        <p className="mt-2 max-w-2xl text-sm text-slate-400">Click a year header to see exactly which students have read which lessons.</p>
      </div>

      <div className="-mb-6 text-right text-[10px] uppercase tracking-widest text-cyan-300/70 sm:hidden">swipe table →</div>
      <div className="relative rounded-md border border-white/10 bg-black/40" data-testid="roster-table">
        <div className="scroll-x-snap overflow-x-auto">
        <div className="min-w-[420px]">
        <div className="grid grid-cols-[1fr_60px_60px_60px_90px] items-center gap-3 border-b border-white/10 bg-white/5 px-4 py-3 text-[10px] uppercase tracking-widest text-slate-400">
          <div>Student</div>
          {[1, 2, 3].map((y) => (
            <button
              key={y}
              onClick={() => setOpenYear(openYear === y ? null : y)}
              data-testid={`roster-year-header-${y}`}
              className={`text-center hover:text-cyan-300 ${openYear === y ? "text-cyan-300" : ""}`}
            >
              Y{y} ↓
            </button>
          ))}
          <div className="text-right">Total</div>
        </div>
        {students.length === 0 && (
          <div className="p-10 text-center text-sm text-slate-500">
            No students have joined your class yet. Share your class code from the Teacher page.
          </div>
        )}
        {students.map((s) => (
          <div key={s.id} className="grid grid-cols-[1fr_60px_60px_60px_90px] items-center gap-3 border-b border-white/5 px-4 py-3 text-sm last:border-b-0" data-testid={`roster-row-${s.id}`}>
            <div>
              <div className="font-semibold text-white">{s.name}</div>
              <div className="text-xs text-slate-500">{s.email}</div>
            </div>
            {[1, 2, 3].map((y) => (
              <div key={y} className="text-center font-mono-num text-slate-300">{s.reads_by_year[y] || 0}</div>
            ))}
            <div className="text-right">
              <span className="rounded-md border border-cyan-400/40 bg-cyan-500/10 px-2 py-1 font-mono-num text-cyan-200">{s.total_reads}</span>
            </div>
          </div>
        ))}
        </div>
        </div>
      </div>

      {openYear && <YearDetail year={openYear} onClose={() => setOpenYear(null)} />}
    </div>
  );
}
